import { ref, update } from "firebase/database";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import flowerVase from "../../assets/images/flowervase.svg";
import { rtdb } from "../../firebase";
import { useFetchUserDetails } from "../../hooks/queries/user";
import { formatDate } from "../../utils/helpers";
import {
    AppNotification,
    AppNotificationType,
} from "../../utils/types/notifications";
import Notification from "./Notification/Notification";
import { useAppSelector } from "../../hooks/useRedux";
import Loader from "../../shared-ui/Loader/Loader";
import ListState from "../ListState/ListState";

interface NotificationBoxProps {
    /** Notifications loading prop */
    loading: boolean;
    /** Error when fetching notifications */
    isError: boolean;
    /** Notifications data */
    data: AppNotification[];
    /** Type of notification */
    type: "not_reminder" | "reminder";
}

function getTimeLapse(isoDateTime: string): string {
    return formatDate(isoDateTime, true, "MM/dd/yyyy, h:mm a");
}

function sortNotificationBySentDate(
    notifications: AppNotification[]
): AppNotification[] {
    return notifications.sort((a, b) =>
        new Date(a.sent_at).getTime() > new Date(b.sent_at).getTime() ? -1 : 1
    );
}

export default function NotificationBox({
    loading,
    isError,
    data: notifications,
    type,
}: NotificationBoxProps) {
    const loggedInUser = useFetchUserDetails();
    const [isExecutingMarkAsRead, setIsExecutingMarkAsRead] =
        React.useState(false);
    const navigate = useNavigate();

    const { practice } = useAppSelector((state) => state.userPractice);

    const markNotificationAsRead = async (
        item: AppNotification,
        isRunning: boolean
    ) => {
        if (item.is_read || isRunning) {
            return;
        }

        setIsExecutingMarkAsRead(true);
        const notificationDocPath = `notifications/${
            practice?.practice_id || ""
        }/${loggedInUser.data?.user_id}/${item.id}`;
        const updatedNotificationItem: AppNotification = {
            ...item,
            is_read: true,
        };
        try {
            await update(
                ref(rtdb, notificationDocPath),
                updatedNotificationItem
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log("Error making notification as read", e);
        } finally {
            setIsExecutingMarkAsRead(false);
        }
    };

    const unreadNotificationCount = notifications.filter(
        (n) => !n.is_read
    ).length;

    return (
        <div className="w-full h-72 overflow-y-auto">
            <div className="h-full">
                <div className="flex items-center justify-between pb-2 [border-bottom:1px_solid_var(--color-stroke)]">
                    <div className="flex items-center gap-x-2">
                        <h3 className="text-title font-semibold">
                            Notifications
                        </h3>
                        {unreadNotificationCount > 0 && (
                            <span className="flex items-center justify-center size-4 text-caption font-medium rounded-full bg-danger text-white">
                                {unreadNotificationCount}
                            </span>
                        )}
                    </div>
                    {/* <button className={styles.mark_all}>
                        Mark all as Read
                    </button> */}
                </div>
                {loading && (
                    <div>
                        <Loader wrapperClassName="h-[150px]" />
                    </div>
                )}
                {isError && (
                    <ListState
                        isError
                        errorMsg="Error fetching notifications"
                        stateHelperText="Please try again later"
                        context="general"
                        cardClass="h-[150px]"
                    />
                )}
                {notifications.length === 0 && (
                    <div className="flex flex-col gap-y-3 pt-3">
                        <img
                            src={flowerVase}
                            alt="illustration for empty state"
                            className="h-28"
                        />
                        <ListState
                            isError={false}
                            emptyMessage="No notifications found"
                            stateHelperText="Looks like you haven't received any
                            notification yet."
                            context="general"
                            cardClass="h-full"
                        />
                    </div>
                )}
                {notifications.length > 0 && (
                    <ul className="mt-3">
                        {sortNotificationBySentDate(notifications).map(
                            (item) => {
                                const onNotificationClick = async () => {
                                    await markNotificationAsRead(
                                        item,
                                        isExecutingMarkAsRead
                                    );
                                    switch (item.type) {
                                        case AppNotificationType.ASSIGNED_TODO: {
                                            if (item.metadata?.todo_id) {
                                                navigate(
                                                    `/tasks?&task_id=${item.metadata.todo_id}`
                                                );
                                            }
                                            break;
                                        }
                                        case AppNotificationType.NEW_NOTE_MESSAGE: {
                                            if (
                                                !item.metadata?.client_id ||
                                                !item.metadata?.note_id
                                            ) {
                                                break;
                                            }
                                            navigate(
                                                `/notes/${item.metadata.client_id}/${item.metadata.note_id}?tab=messages`
                                            );
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                };
                                // const onNotificationHover = async () => {
                                //     await markNotificationAsRead(
                                //         item,
                                //         isExecutingMarkAsRead
                                //     );
                                // };
                                return (
                                    <li
                                        key={item.id}
                                        className="last:mb-0 mb-3"
                                    >
                                        <Notification
                                            title={item.subject}
                                            details={item.description}
                                            timeLaspe={getTimeLapse(
                                                item.sent_at
                                            )}
                                            isUnread={!item.is_read}
                                            type={type}
                                            onClick={onNotificationClick}
                                        />
                                    </li>
                                );
                            }
                        )}
                    </ul>
                )}
            </div>
        </div>
    );
}
